import { useEffect, useState } from "react";
import { SubCategoriesViewModel } from "../../viewmodels/subCategories/SubCategoriesViewModel";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../store/actions/index";
import { classNames, withRouter } from "../../shared/utility";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import MobileCardsTemplate from "../../components/templates/MobileCardsTemplate";

// Other Components
import SlideOver from "../../components/slideOver/SlideOver";
import EmptyModal from "../../components/modals/EmptyModal";

function SubCategories(props) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    emptyModal,
    setEmptyModelOpen,
    getSubCategories,
    loading,
    subCategories,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    askDeleteSubCategory,
  } = SubCategoriesViewModel(logout);

  const [currentPage, setCurrentPage] = useState(1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    getSubCategories(null, currentPage, filters);
    getPrimaryCategoriesForDropdown();
  }, []);

  // Search handler
  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    getSubCategories(term || null, 1, filters);
  };

  // Pagination handler
  const handlePageChange = (event) => {
    const newPage = event.page + 1;
    setCurrentPage(newPage);
    setFirst(event.first);
    setRows(event.rows);
    getSubCategories(searchTerm || null, newPage, filters);
  };

  // Filter configuration
  const filterConfig = {
    dropdown: true,
    components: filterComponents,
    onReset: () => {
      setCurrentPage(1);
      setFirst(0);
      resetFilters();
    },
    onApply: () => {
      setCurrentPage(1);
      setFirst(0);
      getSubCategories(searchTerm || null, 1, filters);
    },
  };

  // Row click handler
  const handleRowClick = (category) => {
    navigate(`/sub-categories/${category.id}`);
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Add Sub Category",
      onClick: () => {
        window.location = "/sub-categories/new";
      },
      className: "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500"
    }
  ];

  // Column configurations for DataTable
  const columns = [
    {
      field: "name",
      header: "Sub Category Name",
      style: { width: '40%' },
      body: (rowData) => (
        <span className={classNames(
          "font-semibold apollo-subheading",
          "text-gray-900"
        )}>
          {rowData.name}
        </span>
      )
    },
    {
      field: "parent_cat_name",
      header: "Parent Category",
      style: { width: '30%' },
      body: (rowData) => (
        <span className="apollo-body">
          {rowData.parent_cat_name}
        </span>
      )
    },
    {
      header: "Actions",
      style: { width: '30%' },
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <Link
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            to={`/sub-categories/${rowData.id}`}
          >
            View
          </Link>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              askDeleteSubCategory(rowData);
            }}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  // Mobile cards component
  const SubCategoriesMobileCards = ({ data }) => (
    <MobileCardsTemplate
      data={data}
      onCardClick={(category) => navigate(`/sub-categories/${category.id}`)}
      onEdit={(category) => navigate(`/sub-categories/${category.id}`)}
      editRoute="/sub-categories"
      emptyMessage="No sub categories found"
      cardContent={(category) => (
        <div className="flex flex-col space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-h6 apollo-heading text-primary-600 mb-1">
                {category.name}
              </h3>
              <p className="text-body-2 apollo-subheading">
                {category.parent_cat_name}
              </p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2">
            {category.parent_cat_name && (
              <div className="flex justify-between text-body-2 apollo-body">
                <span>Parent Category:</span>
                <span>{category.parent_cat_name}</span>
              </div>
            )}
          </div>
        </div>
      )}
      actionButtons={(category) => (
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/sub-categories/${category.id}`} className="flex-1">
            <button
              className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              View
            </button>
          </Link>
          <button
            type="button"
            onClick={() => askDeleteSubCategory(category)}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )}
    />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Product Sub Categories"
        description="Manage your product sub categories"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        columns={columns}
        data={subCategories.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: subCategories.total || 0,
          template: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} sub categories"
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by category name"
        MobileCardsComponent={SubCategoriesMobileCards}
        emptyMessage="No sub categories found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SubCategories)
);
